import { useEffect, useState } from 'react';
import { ArrowUpTrayIcon, CheckIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Notification, { NotificationContainer } from '../../../components/Notification';
import { Drawer } from '../../../components/ui/Drawer';
import thirdPartiesService from '../../../services/compliance/thirdParties.service';
import {
  ThirdParty,
  ThirdPartyCreate,
  ThirdPartyUpdate,
} from '../../../types/compliance';

type Mode = 'create' | 'edit';

export interface ThirdPartyDrawerProps {
  mode: Mode;
  thirdPartyId: string | null;
  open: boolean;
  onClose: () => void;
  onSaved: (tp: ThirdParty) => void;
  onDeleted: () => void;
  /** Fires when the user clicks "Bulk Import" in create mode. The parent is
   *  expected to close this drawer and open ThirdPartyBulkImportDrawer. */
  onBulkImport?: () => void;
}

interface FormState {
  name: string;
  legal_entity_name: string;
  website: string;
}

const emptyForm: FormState = {
  name: '',
  legal_entity_name: '',
  website: '',
};

function thirdPartyToFormState(tp: ThirdParty): FormState {
  return {
    name: tp.name || '',
    legal_entity_name: tp.legal_entity_name || '',
    website: tp.website || '',
  };
}

export default function ThirdPartyDrawer({
  mode: initialMode,
  thirdPartyId: initialId,
  open,
  onClose,
  onSaved,
  onDeleted,
  onBulkImport,
}: ThirdPartyDrawerProps) {
  // Internal mode/id — flips from 'create' to 'edit' after first save.
  const [mode, setMode] = useState<Mode>(initialMode);
  const [currentId, setCurrentId] = useState<string | null>(initialId);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [thirdParty, setThirdParty] = useState<ThirdParty | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);

  // Reset + load whenever the drawer opens with new props. One effect
  // reading initialMode/initialId directly avoids a race where a stale
  // loadExisting closure from a prior edit session overwrites a freshly
  // reset create form.
  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setCurrentId(initialId);
    setForm(emptyForm);
    setThirdParty(null);
    if (initialMode === 'edit' && initialId) {
      (async () => {
        setLoading(true);
        try {
          const tp = await thirdPartiesService.get(initialId);
          setThirdParty(tp);
          setForm(thirdPartyToFormState(tp));
        } catch (error: any) {
          const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
          setErrorNotification({ title: 'Failed to load third party', message: msg });
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [open, initialMode, initialId]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const validate = (): string | null => {
    if (!form.name.trim()) return 'Name is required.';
    if (form.name.length > 255) return 'Name must be 255 characters or fewer.';
    if (form.legal_entity_name.length > 255)
      return 'Legal Entity Name must be 255 characters or fewer.';
    if (form.website.length > 500) return 'Website must be 500 characters or fewer.';
    return null;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const err = validate();
    if (err) {
      setErrorNotification({ title: 'Validation Error', message: err });
      return;
    }
    setSaving(true);
    try {
      if (mode === 'edit' && currentId) {
        const patch: ThirdPartyUpdate = {
          name: form.name.trim(),
          legal_entity_name: form.legal_entity_name,
          website: form.website,
        };
        const updated = await thirdPartiesService.update(currentId, patch);
        onSaved(updated);
        onClose();
      } else {
        const body: ThirdPartyCreate = {
          name: form.name.trim(),
          legal_entity_name: form.legal_entity_name,
          website: form.website,
        };
        const created = await thirdPartiesService.create(body);
        onSaved(created);
        onClose();
      }
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Save', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!thirdParty) return;
    if (!window.confirm(`Are you sure you want to delete "${thirdParty.name}"?`)) return;
    try {
      await thirdPartiesService.remove(thirdParty.id);
      onDeleted();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Delete', message: msg });
    }
  };

  const isEdit = mode === 'edit';

  const inputCls =
    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white';
  const labelCls = 'block text-sm font-medium text-label mb-1';

  const title = (
    <span className="truncate max-w-md">
      {isEdit && thirdParty ? thirdParty.name : 'New Third Party'}
    </span>
  );

  const footer = (
    <div className="flex items-center justify-between gap-3">
      <div>
        {isEdit && thirdParty && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <TrashIcon className="w-4 h-4" /> Delete
          </button>
        )}
        {!isEdit && onBulkImport && (
          <button
            type="button"
            onClick={onBulkImport}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            <ArrowUpTrayIcon className="w-4 h-4" /> Bulk Import
          </button>
        )}
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => handleSubmit()}
          disabled={saving || loading}
          className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckIcon className="w-4 h-4" />
              {isEdit ? 'Save Changes' : 'Create Third Party'}
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Drawer
        isOpen={open}
        onClose={onClose}
        title={title}
        width="50vw"
        footer={footer}
        disableClickOutside
        contentClassName="relative flex-1 overflow-y-auto flex flex-col"
      >
        <div className="flex-1 px-4 sm:px-6 py-4 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={labelCls}>
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  maxLength={255}
                  required
                  className={inputCls}
                  disabled={saving}
                />
              </div>

              <div>
                <label className={labelCls}>Legal Entity Name</label>
                <input
                  type="text"
                  value={form.legal_entity_name}
                  onChange={(e) => set('legal_entity_name', e.target.value)}
                  maxLength={255}
                  placeholder="e.g., Acme Corporation, Inc."
                  className={inputCls}
                  disabled={saving}
                />
              </div>

              <div>
                <label className={labelCls}>Website</label>
                <input
                  type="url"
                  value={form.website}
                  onChange={(e) => set('website', e.target.value)}
                  maxLength={500}
                  placeholder="https://example.com"
                  className={inputCls}
                  disabled={saving}
                />
              </div>

              {/* Hidden submit input so Enter within inputs triggers submit */}
              <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1}>
                <XMarkIcon className="w-0 h-0" />
              </button>
            </form>
          )}
        </div>
      </Drawer>

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}
    </>
  );
}
